/**
 * Spaces — named containers for chats inside one organization ("Orion Credit
 * Fund", "AML remediation 2026"), from `/api/amoreg/spaces`.
 *
 * Same idiom as `state/automations.ts`: a per-org cached atom over the
 * workspace server, mutations POST and then refresh it. The server decides
 * who may see and change what; this module only renders its answers.
 *
 * Decoding is forward-compatible: spaces and assignments decode one by one
 * (a malformed entry is dropped, not fatal) and an unknown colour reads as
 * `slate`. A server without the feature answers 404 (or the SPA fallback's
 * HTML), which reads as `available: false` so the UI can hide Spaces
 * honestly instead of showing an empty list.
 *
 * @module state/spaces
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo, useSyncExternalStore } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

export const SPACE_COLORS = ["violet", "emerald", "amber", "sky", "rose", "slate"] as const;
export type SpaceColor = (typeof SPACE_COLORS)[number];

/** The client organization a client space serves; `name` is null when the directory is down. */
export interface SpaceClient {
  readonly organizationId: string;
  readonly name: string | null;
}

export interface Space {
  readonly id: string;
  readonly name: string;
  readonly color: SpaceColor;
  /** Empty when the server did not say (a guest's view of the space). */
  readonly createdAt: string;
  readonly description: string | null;
  /** Null for a plain space, and always for a guest. */
  readonly client: SpaceClient | null;
}

/** Filled glyph classes per colour (the space's initial on its colour). */
export const SPACE_COLOR_CLASS: Record<SpaceColor, string> = {
  violet: "bg-violet-600 text-white",
  emerald: "bg-emerald-600 text-white",
  amber: "bg-amber-600 text-white",
  sky: "bg-sky-600 text-white",
  rose: "bg-rose-600 text-white",
  slate: "bg-slate-600 text-white",
};

/** The glyph letter: the name's first letter or digit, uppercased; `?` if none. */
export function spaceInitial(name: string): string {
  const match = /[\p{L}\p{N}]/u.exec(name);
  return match ? match[0].toLocaleUpperCase() : "?";
}

export const normalizeSpaceColor = (color: string | undefined): SpaceColor =>
  color !== undefined && (SPACE_COLORS as ReadonlyArray<string>).includes(color)
    ? (color as SpaceColor)
    : "slate";

const WireSpaceSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  color: Schema.optional(Schema.String),
  createdAt: Schema.optional(Schema.String),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  client: Schema.optional(Schema.Unknown),
});
const WireSpaceClientSchema = Schema.Struct({
  organizationId: Schema.String,
  name: Schema.optional(Schema.NullOr(Schema.String)),
});
const WireThreadSpaceSchema = Schema.Struct({
  threadId: Schema.String,
  spaceId: Schema.String,
});
/** The envelope only; entries stay unknown so each decodes on its own. */
const WireSnapshotSchema = Schema.Struct({
  spaces: Schema.Array(Schema.Unknown),
  threadSpaces: Schema.optional(Schema.Array(Schema.Unknown)),
});

const decodeWireSnapshot = Schema.decodeUnknownOption(WireSnapshotSchema);
const decodeWireSpace = Schema.decodeUnknownOption(WireSpaceSchema);
const decodeWireThreadSpace = Schema.decodeUnknownOption(WireThreadSpaceSchema);
const decodeWireSpaceClient = Schema.decodeUnknownOption(WireSpaceClientSchema);

/** A malformed client link reads as no link: the page then shows a plain space. */
const toSpaceClient = (input: unknown): SpaceClient | null =>
  Option.match(decodeWireSpaceClient(input), {
    onNone: () => null,
    onSome: (wire) => ({ organizationId: wire.organizationId, name: wire.name ?? null }),
  });

export const toSpace = (input: unknown): Space | null =>
  Option.match(decodeWireSpace(input), {
    onNone: () => null,
    onSome: (wire) => ({
      id: wire.id,
      name: wire.name,
      color: normalizeSpaceColor(wire.color),
      createdAt: wire.createdAt ?? "",
      description: wire.description?.trim() ? wire.description : null,
      client: toSpaceClient(wire.client),
    }),
  });

export interface SpacesSnapshot {
  readonly spaces: ReadonlyArray<Space>;
  readonly spaceIdByThreadId: ReadonlyMap<string, string>;
  /** False when the server has no Spaces feature. */
  readonly available: boolean;
}

const UNAVAILABLE: SpacesSnapshot = {
  spaces: [],
  spaceIdByThreadId: new Map(),
  available: false,
};

/**
 * Decode a `GET /api/amoreg/spaces` body. Malformed entries are dropped, and
 * an assignment to a space the list does not contain is ignored. Null when
 * the envelope itself is not a spaces answer.
 */
export function decodeSpacesSnapshot(json: unknown): SpacesSnapshot | null {
  const wire = Option.getOrNull(decodeWireSnapshot(json));
  if (wire === null) return null;
  const spaces = wire.spaces.map(toSpace).filter((space): space is Space => space !== null);
  const known = new Set(spaces.map((space) => space.id));
  const spaceIdByThreadId = new Map<string, string>();
  for (const entry of wire.threadSpaces ?? []) {
    const decoded = Option.getOrNull(decodeWireThreadSpace(entry));
    if (decoded !== null && known.has(decoded.spaceId)) {
      spaceIdByThreadId.set(decoded.threadId, decoded.spaceId);
    }
  }
  return { spaces, spaceIdByThreadId, available: true };
}

/**
 * Each filed chat's space, keyed by thread id, so a list renders every row's
 * mark from one lookup instead of a search per row. A chat filed in a space
 * the list does not contain has no entry.
 */
export function spaceByThreadId(
  spaces: ReadonlyArray<Space>,
  spaceIdByThreadId: ReadonlyMap<string, string>,
): ReadonlyMap<string, Space> {
  const byId = new Map(spaces.map((space) => [space.id, space]));
  const result = new Map<string, Space>();
  for (const [threadId, spaceId] of spaceIdByThreadId) {
    const space = byId.get(spaceId);
    if (space) result.set(threadId, space);
  }
  return result;
}

class SpacesRequestError extends Schema.TaggedErrorClass<SpacesRequestError>()(
  "SpacesRequestError",
  { status: Schema.Number },
) {}

const spacesUrl = (organizationId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/spaces", { organizationId });

const isJson = (response: Response) =>
  (response.headers.get("content-type") ?? "").includes("application/json");

const fetchSpaces = (organizationId: string): Effect.Effect<SpacesSnapshot, unknown> =>
  Effect.gen(function* () {
    const response = yield* Effect.tryPromise(() =>
      fetch(spacesUrl(organizationId), { credentials: "include" }),
    );
    // 404, or a dev server's SPA fallback answering an unrouted path with
    // HTML: this server predates Spaces.
    if (response.status === 404 || (response.ok && !isJson(response))) {
      return UNAVAILABLE;
    }
    if (!response.ok) {
      return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    }
    const json = yield* Effect.tryPromise(() => response.json());
    const snapshot = decodeSpacesSnapshot(json);
    if (snapshot === null) {
      return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    }
    return snapshot;
  });

const spacesRuntime = Atom.runtime(Layer.empty);

const spacesAtom = Atom.family((organizationId: string) =>
  spacesRuntime
    .atom(organizationId.length === 0 ? Effect.succeed(UNAVAILABLE) : fetchSpaces(organizationId))
    .pipe(Atom.withLabel(`web-amoreg:spaces:${organizationId}`)),
);

/** Refetch an organization's spaces, e.g. after a pending assignment lands. */
export function refreshOrgSpaces(organizationId: string): void {
  appAtomRegistry.refresh(spacesAtom(organizationId));
}

/**
 * The sentence to show for a failed write: the server's own text when it is
 * a short plain message (400/403/409 answers are written for people), or a
 * JSON `message`; otherwise the fallback.
 */
export function spacesErrorMessage(body: string, fallback: string): string {
  const trimmed = body.trim();
  if (trimmed.length === 0) return fallback;
  try {
    const parsed = JSON.parse(trimmed) as { message?: unknown };
    return typeof parsed?.message === "string" && parsed.message.length > 0
      ? parsed.message
      : fallback;
  } catch {
    // Not JSON: plain text is usable unless it is markup or a bare status word.
    if (trimmed.startsWith("<") || trimmed.length > 200) return fallback;
    if (/^(unauthorized|forbidden|not found)$/i.test(trimmed)) return fallback;
    return trimmed;
  }
}

export type CreateSpaceOutcome =
  | { readonly ok: true; readonly space: Space }
  | { readonly ok: false; readonly message: string };

const CREATE_FALLBACK = "The space could not be created.";

/** `POST /api/amoreg/spaces?organizationId=…` `{name}`. Usable outside React. */
export async function createSpaceRequest(
  organizationId: string,
  name: string,
): Promise<CreateSpaceOutcome> {
  try {
    const response = await fetch(spacesUrl(organizationId), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      return { ok: false, message: spacesErrorMessage(body, CREATE_FALLBACK) };
    }
    const json = (await response.json()) as { space?: unknown } | null;
    const space = toSpace(json?.space);
    return space ? { ok: true, space } : { ok: false, message: CREATE_FALLBACK };
  } catch {
    return { ok: false, message: CREATE_FALLBACK };
  }
}

const ASSIGN_FALLBACK = "The chat could not be moved.";

/** The last assignment issued per thread; the next one waits for it. */
const assignmentTails = new Map<string, Promise<string | null>>();

/**
 * `POST /api/amoreg/thread-space?threadId=…` `{spaceId}` — `null` takes the
 * chat out of its space. Resolves to an error message, or null on success.
 * The server derives the organization from the thread. Usable outside React.
 *
 * Assignments for one thread are sent one after another, in the order they
 * were made: the server's write is a plain upsert, so two in flight at once
 * (a queued Home choice being filed while the person picks again) could land
 * out of order and leave the older space. In turn, the latest choice is the
 * last write.
 */
export function assignThreadSpaceRequest(threadId: string, spaceId: string | null): Promise<string | null> {
  // One chat's moves land in the order they were asked for: the filing of a
  // just-started chat must not overwrite a move the person made a moment later.
  const previous = threadSpaceWrites.get(threadId) ?? Promise.resolve(null);
  const write = previous.then(() => postThreadSpace(threadId, spaceId));
  threadSpaceWrites.set(threadId, write);
  void write.finally(() => {
    if (threadSpaceWrites.get(threadId) === write) threadSpaceWrites.delete(threadId);
  });
  return write;
}

const threadSpaceWrites = new Map<string, Promise<string | null>>();

async function postThreadSpace(threadId: string, spaceId: string | null): Promise<string | null> {
  try {
    const response = await fetch(
      resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-space", { threadId }),
      {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ spaceId }),
      },
    );
    if (response.ok) return null;
    if (response.status === 404) return "That chat or space no longer exists.";
    const body = await response.text().catch(() => "");
    return spacesErrorMessage(body, ASSIGN_FALLBACK);
  } catch {
    return ASSIGN_FALLBACK;
  }
}

/**
 * Space choices made for a chat before its server thread exists (a message
 * sent from a space starts the thread). Keyed by the pre-allocated thread id;
 * whoever watches threads arrive calls `takePendingThreadSpaces` and posts
 * each with `assignThreadSpaceRequest`. A later request for the same thread
 * replaces the earlier one; the composer's space picker reads the choice
 * with `useRequestedThreadSpaceId`.
 */
export interface PendingThreadSpace {
  readonly threadId: string;
  readonly organizationId: string;
  readonly spaceId: string;
}
const pendingThreadSpaces = new Map<string, PendingThreadSpace>();
const pendingThreadSpaceListeners = new Set<() => void>();

function notifyPendingThreadSpaces(): void {
  for (const listener of pendingThreadSpaceListeners) listener();
}

export function requestThreadSpace(
  threadId: string,
  organizationId: string,
  spaceId: string,
): void {
  const current = pendingThreadSpaces.get(threadId);
  if (current?.organizationId === organizationId && current.spaceId === spaceId) return;
  pendingThreadSpaces.set(threadId, { threadId, organizationId, spaceId });
  notifyPendingThreadSpaces();
}

/** Forgets a chat's space choice: it starts in no space. */
export function cancelThreadSpaceRequest(threadId: string): void {
  if (pendingThreadSpaces.delete(threadId)) notifyPendingThreadSpaces();
}

/**
 * The composer's space choice. A draft queues it until its thread exists; a
 * started chat moves now, after dropping any choice still queued for it, so
 * the queued one cannot land later over this one. Resolves to an error
 * message, or null.
 */
export async function chooseThreadSpace(input: {
  readonly draft: boolean;
  readonly threadId: string;
  readonly organizationId: string;
  readonly spaceId: string | null;
  readonly assign: (threadId: string, spaceId: string | null) => Promise<string | null>;
}): Promise<string | null> {
  if (input.draft) {
    if (input.spaceId) requestThreadSpace(input.threadId, input.organizationId, input.spaceId);
    else cancelThreadSpaceRequest(input.threadId);
    return null;
  }
  cancelThreadSpaceRequest(input.threadId);
  return input.assign(input.threadId, input.spaceId);
}

/** The space chosen for a chat that does not exist yet, or null. */
export function requestedThreadSpaceId(threadId: string | null): string | null {
  return threadId === null ? null : (pendingThreadSpaces.get(threadId)?.spaceId ?? null);
}

function subscribePendingThreadSpaces(listener: () => void): () => void {
  pendingThreadSpaceListeners.add(listener);
  return () => pendingThreadSpaceListeners.delete(listener);
}

export function useRequestedThreadSpaceId(threadId: string | null): string | null {
  return useSyncExternalStore(subscribePendingThreadSpaces, () => requestedThreadSpaceId(threadId));
}

/** Returns, and forgets, the pending requests whose thread now exists. */
export function takePendingThreadSpaces(
  existingThreadIds: ReadonlySet<string>,
): Array<PendingThreadSpace> {
  const ready: Array<PendingThreadSpace> = [];
  for (const [threadId, pending] of pendingThreadSpaces) {
    if (existingThreadIds.has(threadId)) {
      ready.push(pending);
      pendingThreadSpaces.delete(threadId);
    }
  }
  if (ready.length > 0) notifyPendingThreadSpaces();
  return ready;
}

export interface OrgSpacesView {
  readonly spaces: ReadonlyArray<Space>;
  readonly spaceIdByThreadId: ReadonlyMap<string, string>;
  /** True until the first answer arrives. */
  readonly isPending: boolean;
  /** False when the server has no Spaces (404) or no organization is chosen. */
  readonly available: boolean;
  /** True when the last fetch failed (network / server error); retry with refresh. */
  readonly failed: boolean;
  readonly refresh: () => void;
  readonly createSpace: (name: string) => Promise<CreateSpaceOutcome>;
  /** Resolves to an error message, or null on success. */
  readonly assignThreadSpace: (threadId: string, spaceId: string | null) => Promise<string | null>;
}

export function useOrgSpaces(organizationId: string | null): OrgSpacesView {
  const atom = useMemo(() => spacesAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const createSpace = useCallback(
    async (name: string): Promise<CreateSpaceOutcome> => {
      if (!organizationId) return { ok: false, message: "Choose an organization first." };
      const outcome = await createSpaceRequest(organizationId, name);
      if (outcome.ok) refresh();
      return outcome;
    },
    [organizationId, refresh],
  );

  const assignThreadSpace = useCallback(
    async (threadId: string, spaceId: string | null): Promise<string | null> => {
      const error = await assignThreadSpaceRequest(threadId, spaceId);
      if (error === null) refresh();
      return error;
    },
    [refresh],
  );

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    const failed = result._tag === "Failure";
    return {
      spaces: snapshot?.spaces ?? UNAVAILABLE.spaces,
      spaceIdByThreadId: snapshot?.spaceIdByThreadId ?? UNAVAILABLE.spaceIdByThreadId,
      isPending: result.waiting && snapshot === null,
      // A failed fetch is not "this server has no Spaces": keep them visible
      // so the failure can be shown and retried.
      available: organizationId !== null && ((snapshot?.available ?? false) || failed),
      failed,
      refresh,
      createSpace,
      assignThreadSpace,
    };
  }, [assignThreadSpace, createSpace, organizationId, refresh, result]);
}

/* ------------------------------------------------------------------ */
/* One space: GET/POST /api/amoreg/space, POST /api/amoreg/space-client */
/* ------------------------------------------------------------------ */

/**
 * What the client organization's people (space guests) may see of its
 * program, key by key. The server owns the list (`amoreg/spaces.ts`); every
 * key is off until a host admin turns it on.
 */
export const CLIENT_ACCESS_KEYS = [
  "policies",
  "procedures",
  "versions",
  "findings",
  "findingBasis",
  "recordUpdates",
  "regimes",
  "knowledge",
] as const;
export type ClientAccessKey = (typeof CLIENT_ACCESS_KEYS)[number];
export type ClientAccess = { readonly [K in ClientAccessKey]: boolean };

/** Every key off: the server's default, and the reading of anything unreadable. */
export const CLIENT_ACCESS_DENY_ALL: ClientAccess = Object.freeze(
  Object.fromEntries(CLIENT_ACCESS_KEYS.map((key) => [key, false])) as Record<ClientAccessKey, boolean>,
);

/** Only `true` counts as on; a missing, unknown or damaged value is off. */
export function toClientAccess(input: unknown): ClientAccess {
  const record =
    input !== null && typeof input === "object" && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : {};
  return Object.fromEntries(CLIENT_ACCESS_KEYS.map((key) => [key, record[key] === true])) as Record<
    ClientAccessKey,
    boolean
  >;
}

/** The session's standing in a space, as the server reports it. */
export type SpaceSessionAccess =
  | { readonly kind: "staff"; readonly canManage: boolean }
  | { readonly kind: "guest"; readonly role: "viewer" | "editor" };

export interface SpaceDetail {
  readonly space: Space;
  readonly access: SpaceSessionAccess;
  /** Staff only: what the client is shown. Null for a guest. */
  readonly clientAccess: ClientAccess | null;
  /** The host organization may link clients and admit guests (`AMOREG_SPACES_ORG_IDS`). */
  readonly clientSpaces: boolean;
}

const WireSpaceDetailSchema = Schema.Struct({
  space: Schema.Unknown,
  access: Schema.Union([
    Schema.Struct({ kind: Schema.Literal("staff"), canManage: Schema.Boolean }),
    Schema.Struct({ kind: Schema.Literal("guest"), role: Schema.Literals(["viewer", "editor"]) }),
  ]),
  clientSpaces: Schema.optional(Schema.Boolean),
});
const decodeWireSpaceDetail = Schema.decodeUnknownOption(WireSpaceDetailSchema);

/**
 * Decode a `GET/POST /api/amoreg/space` body. A staff answer carries the
 * client access settings on the space; a guest's carries none. Null when the
 * body is not a space answer.
 */
export function decodeSpaceDetail(json: unknown): SpaceDetail | null {
  const wire = Option.getOrNull(decodeWireSpaceDetail(json));
  if (wire === null) return null;
  const space = toSpace(wire.space);
  if (space === null) return null;
  if (wire.access.kind === "guest") {
    // A guest never learns the client link, whatever the body says.
    return { space: { ...space, client: null }, access: wire.access, clientAccess: null, clientSpaces: false };
  }
  return {
    space,
    access: wire.access,
    clientAccess: toClientAccess((wire.space as { clientAccess?: unknown } | null)?.clientAccess),
    clientSpaces: wire.clientSpaces ?? false,
  };
}

/** The page's view of one space: found, not found (also: no access), or a failure. */
export type SpaceLoad =
  | { readonly kind: "found"; readonly detail: SpaceDetail }
  | { readonly kind: "not-found" }
  | { readonly kind: "unavailable" };

const spaceUrl = (spaceId: string) => resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space", { spaceId });
const spaceClientUrl = (spaceId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-client", { spaceId });

const fetchSpace = (spaceId: string): Effect.Effect<SpaceLoad, unknown> =>
  Effect.gen(function* () {
    const response = yield* Effect.tryPromise(() => fetch(spaceUrl(spaceId), { credentials: "include" }));
    // A missing space and one the session cannot reach answer alike.
    if (response.status === 404) return { kind: "not-found" } as const;
    // A dev server's SPA fallback: this server predates single-space routes.
    if (response.ok && !isJson(response)) return { kind: "unavailable" } as const;
    if (!response.ok) {
      return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    }
    const detail = decodeSpaceDetail(yield* Effect.tryPromise(() => response.json()));
    if (detail === null) {
      return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    }
    return { kind: "found", detail } as const;
  });

const spaceAtom = Atom.family((spaceId: string) =>
  spacesRuntime
    .atom(spaceId.length === 0 ? Effect.succeed<SpaceLoad>({ kind: "not-found" }) : fetchSpace(spaceId))
    .pipe(Atom.withLabel(`web-amoreg:space:${spaceId}`)),
);

export type SpaceWriteOutcome =
  | { readonly ok: true; readonly detail: SpaceDetail }
  | { readonly ok: false; readonly message: string };

export interface SpaceUpdate {
  readonly name?: string;
  /** Blank or null clears it. */
  readonly description?: string | null;
  /** Only the keys that change; the server merges them over the current settings. */
  readonly clientAccess?: Partial<ClientAccess>;
}

const UPDATE_FALLBACK = "The space could not be saved.";
const LINK_FALLBACK = "The client organization could not be changed.";

async function postSpaceWrite(url: string, body: unknown, fallback: string): Promise<SpaceWriteOutcome> {
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      if (response.status === 404) return { ok: false, message: "This space no longer exists." };
      const text = await response.text().catch(() => "");
      return { ok: false, message: spacesErrorMessage(text, fallback) };
    }
    const detail = decodeSpaceDetail(await response.json());
    return detail ? { ok: true, detail } : { ok: false, message: fallback };
  } catch {
    return { ok: false, message: fallback };
  }
}

/** `POST /api/amoreg/space?spaceId=…` `{name?, description?, clientAccess?}` (managers). */
export function updateSpaceRequest(spaceId: string, update: SpaceUpdate): Promise<SpaceWriteOutcome> {
  return postSpaceWrite(spaceUrl(spaceId), update, UPDATE_FALLBACK);
}

/**
 * `POST /api/amoreg/space-client?spaceId=…` `{clientOrganizationId}`: link the
 * client organization, or unlink it with null. Changing it resets every
 * client access setting to off (server rule).
 */
export function setSpaceClientRequest(
  spaceId: string,
  clientOrganizationId: string | null,
): Promise<SpaceWriteOutcome> {
  return postSpaceWrite(spaceClientUrl(spaceId), { clientOrganizationId }, LINK_FALLBACK);
}

export interface SpaceView {
  /** Null until the first answer, and when the space is missing or failed. */
  readonly detail: SpaceDetail | null;
  readonly isPending: boolean;
  /** Missing, or not the session's to see (the server does not say which). */
  readonly notFound: boolean;
  /** The last fetch failed or the server has no single-space routes; retry with refresh. */
  readonly failed: boolean;
  readonly refresh: () => void;
  /** Resolves to an error message, or null on success. */
  readonly update: (update: SpaceUpdate) => Promise<string | null>;
  readonly setClient: (clientOrganizationId: string | null) => Promise<string | null>;
}

/**
 * One space and the session's access to it. `organizationId` is the host
 * organization in the URL: its spaces list is refreshed after a write, so
 * the sidebar and the list show a new name or client at once.
 */
export function useSpace(spaceId: string | null, organizationId: string | null): SpaceView {
  const atom = useMemo(() => spaceAtom(spaceId ?? ""), [spaceId]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const afterWrite = useCallback(
    (outcome: SpaceWriteOutcome): string | null => {
      if (!outcome.ok) return outcome.message;
      refresh();
      if (organizationId) refreshOrgSpaces(organizationId);
      return null;
    },
    [organizationId, refresh],
  );

  const update = useCallback(
    async (change: SpaceUpdate) =>
      spaceId ? afterWrite(await updateSpaceRequest(spaceId, change)) : "No space selected.",
    [afterWrite, spaceId],
  );
  const setClient = useCallback(
    async (clientOrganizationId: string | null) =>
      spaceId ? afterWrite(await setSpaceClientRequest(spaceId, clientOrganizationId)) : "No space selected.",
    [afterWrite, spaceId],
  );

  return useMemo(() => {
    const load = Option.getOrNull(AsyncResult.value(result));
    return {
      detail: load?.kind === "found" ? load.detail : null,
      isPending: result.waiting && load === null && result._tag !== "Failure",
      notFound: load?.kind === "not-found",
      failed: result._tag === "Failure" || load?.kind === "unavailable",
      refresh,
      update,
      setClient,
    };
  }, [refresh, result, setClient, update]);
}

/* ------------------------------------------------------------------ */
/* Guests                                                              */
/* ------------------------------------------------------------------ */

export type SpaceGuestRole = "viewer" | "editor";

/** An active guest grant: one of the client's people, never an organization member. */
export interface SpaceGuest {
  readonly email: string;
  readonly role: SpaceGuestRole;
  readonly invitedBy: string | null;
  readonly createdAt: string;
}

const toSpaceGuest = (input: unknown): SpaceGuest | null => {
  if (input === null || typeof input !== "object") return null;
  const row = input as Record<string, unknown>;
  if (typeof row.email !== "string" || row.email.length === 0) return null;
  return {
    email: row.email,
    role: row.role === "editor" ? "editor" : "viewer",
    invitedBy: typeof row.invitedBy === "string" ? row.invitedBy : null,
    createdAt: typeof row.createdAt === "string" ? row.createdAt : "",
  };
};

/** `{guests: [...]}`, one guest at a time (a malformed entry is dropped), or null. */
export function decodeSpaceGuests(json: unknown): ReadonlyArray<SpaceGuest> | null {
  const guests = (json as { guests?: unknown } | null)?.guests;
  if (!Array.isArray(guests)) return null;
  return guests.map(toSpaceGuest).filter((guest): guest is SpaceGuest => guest !== null);
}

const spaceGuestsUrl = (spaceId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-guests", { spaceId });

/** Guests cannot be listed: no such route on this server, or not the session's to see. */
const NO_GUESTS: { readonly available: false } = { available: false };

type GuestsLoad = { readonly available: true; readonly guests: ReadonlyArray<SpaceGuest> } | typeof NO_GUESTS;

const fetchSpaceGuests = (spaceId: string): Effect.Effect<GuestsLoad, unknown> =>
  Effect.gen(function* () {
    const response = yield* Effect.tryPromise(() => fetch(spaceGuestsUrl(spaceId), { credentials: "include" }));
    if (response.status === 404 || response.status === 403 || (response.ok && !isJson(response))) return NO_GUESTS;
    if (!response.ok) return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    const guests = decodeSpaceGuests(yield* Effect.tryPromise(() => response.json()));
    if (guests === null) return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    return { available: true, guests } as const;
  });

const spaceGuestsAtom = Atom.family((spaceId: string) =>
  spacesRuntime
    .atom(spaceId.length === 0 ? Effect.succeed<GuestsLoad>(NO_GUESTS) : fetchSpaceGuests(spaceId))
    .pipe(Atom.withLabel(`web-amoreg:space-guests:${spaceId}`)),
);

const GUEST_FALLBACK = "The guest could not be changed.";

/**
 * `POST /api/amoreg/space-guests?spaceId=…` `{email, role}` invites (or
 * changes the role of) a guest; `{email, revoke: true}` ends the grant.
 * Managers only; resolves to an error message, or null on success.
 */
export async function spaceGuestRequest(
  spaceId: string,
  body: { readonly email: string; readonly role: SpaceGuestRole } | { readonly email: string; readonly revoke: true },
): Promise<string | null> {
  try {
    const response = await fetch(spaceGuestsUrl(spaceId), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.ok) return null;
    if (response.status === 404) return "This space no longer exists.";
    return spacesErrorMessage(await response.text().catch(() => ""), GUEST_FALLBACK);
  } catch {
    return GUEST_FALLBACK;
  }
}

export interface SpaceGuestsView {
  /** False until the first answer, and when this server or session cannot list guests. */
  readonly available: boolean;
  readonly guests: ReadonlyArray<SpaceGuest>;
  readonly isPending: boolean;
  readonly failed: boolean;
  readonly refresh: () => void;
  readonly invite: (email: string, role: SpaceGuestRole) => Promise<string | null>;
  readonly revoke: (email: string) => Promise<string | null>;
}

/** The space's active guests, and the manager's invite and revoke. */
export function useSpaceGuests(spaceId: string | null): SpaceGuestsView {
  const atom = useMemo(() => spaceGuestsAtom(spaceId ?? ""), [spaceId]);
  const result = useAtomValue(atom);
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  const write = useCallback(
    async (body: Parameters<typeof spaceGuestRequest>[1]) => {
      if (!spaceId) return "No space selected.";
      const failure = await spaceGuestRequest(spaceId, body);
      if (failure === null) refresh();
      return failure;
    },
    [refresh, spaceId],
  );
  const invite = useCallback((email: string, role: SpaceGuestRole) => write({ email, role }), [write]);
  const revoke = useCallback((email: string) => write({ email, revoke: true }), [write]);
  return useMemo(() => {
    const load = Option.getOrNull(AsyncResult.value(result));
    return {
      available: load?.available === true,
      guests: load?.available === true ? load.guests : [],
      isPending: result.waiting && load === null && result._tag !== "Failure",
      failed: result._tag === "Failure",
      refresh,
      invite,
      revoke,
    };
  }, [invite, refresh, result, revoke]);
}

/* ------------------------------------------------------------------ */
/* Documents and their versions; the space's change history            */
/* ------------------------------------------------------------------ */

export interface SpaceDocumentVersion {
  readonly version: number;
  /** A named checkpoint's label; null for an automatic one. */
  readonly label: string | null;
  readonly createdBy: string | null;
  readonly createdAt: string;
}

/** The file a page or sheet was uploaded from. */
export interface SpaceDocumentFile {
  readonly name: string;
  readonly size: number;
  readonly mediaType: string;
}

export interface SpaceDocumentSummary {
  readonly docId: string;
  readonly kind: string;
  readonly title: string | null;
  readonly audience: string;
  readonly createdAt: string;
  readonly createdBy: string | null;
  readonly updatedAt: string;
  /** Null for a document made here (the Board), not uploaded. */
  readonly file: SpaceDocumentFile | null;
  /** Newest first. */
  readonly versions: ReadonlyArray<SpaceDocumentVersion>;
}

const str = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value : null);

const toDocumentVersion = (input: unknown): SpaceDocumentVersion | null => {
  const row = (input ?? {}) as Record<string, unknown>;
  const createdAt = str(row.createdAt);
  if (typeof row.version !== "number" || createdAt === null) return null;
  return { version: row.version, label: str(row.label), createdBy: str(row.createdBy), createdAt };
};

const toDocumentFile = (input: unknown): SpaceDocumentFile | null => {
  const row = (input ?? {}) as Record<string, unknown>;
  const name = str(row.name);
  if (name === null || typeof row.size !== "number") return null;
  return { name, size: row.size, mediaType: str(row.mediaType) ?? "application/octet-stream" };
};

const toDocumentSummary = (input: unknown): SpaceDocumentSummary | null => {
  const row = (input ?? {}) as Record<string, unknown>;
  const docId = str(row.docId);
  const createdAt = str(row.createdAt);
  if (docId === null || createdAt === null) return null;
  const versions = Array.isArray(row.versions) ? row.versions.map(toDocumentVersion) : [];
  return {
    file: toDocumentFile(row.file),
    docId,
    kind: str(row.kind) ?? "page",
    title: str(row.title),
    audience: str(row.audience) ?? "team",
    createdAt,
    createdBy: str(row.createdBy),
    updatedAt: str(row.updatedAt) ?? createdAt,
    versions: versions
      .filter((version): version is SpaceDocumentVersion => version !== null)
      .toSorted((left, right) => right.version - left.version),
  };
};

/** `{documents: [...]}`, one document at a time (a malformed entry is dropped), or null. */
export function decodeSpaceDocuments(json: unknown): ReadonlyArray<SpaceDocumentSummary> | null {
  const documents = (json as { documents?: unknown } | null)?.documents;
  if (!Array.isArray(documents)) return null;
  return documents.map(toDocumentSummary).filter((doc): doc is SpaceDocumentSummary => doc !== null);
}

/** One entry of the space's change history, as the server records it. */
export interface SpaceActivityEvent {
  readonly id: string;
  readonly kind: string;
  /** The person's email, or null for the system and the agent. */
  readonly actor: string | null;
  readonly subject: string | null;
  readonly detail: unknown;
  readonly createdAt: string;
}

const toActivityEvent = (input: unknown): SpaceActivityEvent | null => {
  const row = (input ?? {}) as Record<string, unknown>;
  const id = str(row.id);
  const kind = str(row.kind);
  const createdAt = str(row.createdAt);
  if (id === null || kind === null || createdAt === null) return null;
  return { id, kind, actor: str(row.actor), subject: str(row.subject), detail: row.detail ?? null, createdAt };
};

/** `{events: [...]}`, one event at a time (a malformed entry is dropped), or null. */
export function decodeSpaceActivity(json: unknown): ReadonlyArray<SpaceActivityEvent> | null {
  const events = (json as { events?: unknown } | null)?.events;
  if (!Array.isArray(events)) return null;
  return events.map(toActivityEvent).filter((event): event is SpaceActivityEvent => event !== null);
}

/** A list read that a server without the route (or a session without access) answers as unavailable. */
type ListLoad<A> = { readonly available: true; readonly items: ReadonlyArray<A> } | { readonly available: false };

const fetchSpaceList = <A>(
  url: string,
  decode: (json: unknown) => ReadonlyArray<A> | null,
): Effect.Effect<ListLoad<A>, unknown> =>
  Effect.gen(function* () {
    const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
    if (response.status === 404 || response.status === 403 || (response.ok && !isJson(response))) {
      return { available: false } as const;
    }
    if (!response.ok) return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    const items = decode(yield* Effect.tryPromise(() => response.json()));
    if (items === null) return yield* Effect.fail(new SpacesRequestError({ status: response.status }));
    return { available: true, items } as const;
  });

const spaceDocumentsAtom = Atom.family((spaceId: string) =>
  spacesRuntime
    .atom(
      spaceId.length === 0
        ? Effect.succeed<ListLoad<SpaceDocumentSummary>>({ available: false })
        : fetchSpaceList(resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-documents", { spaceId }), decodeSpaceDocuments),
    )
    .pipe(Atom.withLabel(`web-amoreg:space-documents:${spaceId}`)),
);

const spaceActivityAtom = Atom.family((spaceId: string) =>
  spacesRuntime
    .atom(
      spaceId.length === 0
        ? Effect.succeed<ListLoad<SpaceActivityEvent>>({ available: false })
        : fetchSpaceList(
            resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-activity", { spaceId, limit: "100" }),
            decodeSpaceActivity,
          ),
    )
    .pipe(Atom.withLabel(`web-amoreg:space-activity:${spaceId}`)),
);

export interface SpaceListView<A> {
  /** False until the first answer, and when this server or session cannot list them. */
  readonly available: boolean;
  readonly items: ReadonlyArray<A>;
  readonly isPending: boolean;
  readonly failed: boolean;
  readonly refresh: () => void;
}

function useSpaceList<A>(atom: Atom.Atom<AsyncResult.AsyncResult<ListLoad<A>, unknown>>): SpaceListView<A> {
  const result = useAtomValue(atom);
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return useMemo(() => {
    const load = Option.getOrNull(AsyncResult.value(result));
    return {
      available: load?.available === true,
      items: load?.available === true ? load.items : [],
      isPending: result.waiting && load === null && result._tag !== "Failure",
      failed: result._tag === "Failure",
      refresh,
    };
  }, [refresh, result]);
}

/** The space's documents (the Board, pages, sheets) with their recent versions. */
export function useSpaceDocuments(spaceId: string | null): SpaceListView<SpaceDocumentSummary> {
  return useSpaceList(useMemo(() => spaceDocumentsAtom(spaceId ?? ""), [spaceId]));
}

/** The space's change history, newest first (staff only). */
export function useSpaceActivity(spaceId: string | null): SpaceListView<SpaceActivityEvent> {
  return useSpaceList(useMemo(() => spaceActivityAtom(spaceId ?? ""), [spaceId]));
}

/** Refetch the space's documents, e.g. after an upload or a saved version. */
export function refreshSpaceDocuments(spaceId: string): void {
  appAtomRegistry.refresh(spaceDocumentsAtom(spaceId));
}

/** Refetch the space's change history. */
export function refreshSpaceActivity(spaceId: string): void {
  appAtomRegistry.refresh(spaceActivityAtom(spaceId));
}

/* ------------------------------------------------------------------ */
/* Uploads, downloads and named versions                               */
/* ------------------------------------------------------------------ */

/** The files a space takes (the server decides; this only filters the picker). */
export const SPACE_UPLOAD_ACCEPT = ".md,.markdown,.xlsx,.csv";
/** The server's limit, checked here first so a large file fails at once. */
export const SPACE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

export type SpaceUploadOutcome =
  | { readonly ok: true; readonly document: SpaceDocumentSummary }
  | { readonly ok: false; readonly message: string };

const UPLOAD_FALLBACK = "The file could not be uploaded.";

/** Whether the space takes a file of this name, by its extension. */
export function isSpaceUploadName(name: string): boolean {
  return /\.(md|markdown|xlsx|csv)$/i.test(name);
}

/**
 * `POST /api/amoreg/space-document-upload?spaceId=…&name=…` with the file as
 * the body: a Markdown file becomes a page, a spreadsheet a sheet.
 */
export async function uploadSpaceFileRequest(
  spaceId: string,
  file: { readonly name: string; readonly size: number } & Blob,
): Promise<SpaceUploadOutcome> {
  if (!isSpaceUploadName(file.name)) {
    return { ok: false, message: "Upload a Markdown (.md), Excel (.xlsx) or CSV (.csv) file." };
  }
  if (file.size > SPACE_UPLOAD_MAX_BYTES) return { ok: false, message: "The file is larger than 5 MB." };
  try {
    const response = await fetch(
      resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-document-upload", { spaceId, name: file.name }),
      { method: "POST", credentials: "include", headers: { "content-type": "application/octet-stream" }, body: file },
    );
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      return { ok: false, message: spacesErrorMessage(text, UPLOAD_FALLBACK) };
    }
    const body = (await response.json()) as { document?: unknown };
    const document = toDocumentSummary(body.document);
    return document ? { ok: true, document } : { ok: false, message: UPLOAD_FALLBACK };
  } catch {
    return { ok: false, message: UPLOAD_FALLBACK };
  }
}

/** Where a document downloads: as .md or .xlsx, or the file it was uploaded from. */
export function spaceDocumentDownloadUrl(spaceId: string, docId: string, original = false): string {
  return resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-document-export", {
    spaceId,
    docId,
    ...(original ? { original: "1" } : {}),
  });
}

export type SpaceVersionOutcome =
  | { readonly ok: true; readonly version: SpaceDocumentVersion }
  | { readonly ok: false; readonly message: string };

const VERSION_FALLBACK = "The version could not be saved.";

/** `POST /api/amoreg/space-document-version?spaceId=…&docId=…` `{label?}`. */
export async function saveSpaceDocumentVersionRequest(
  spaceId: string,
  docId: string,
  label: string | null,
): Promise<SpaceVersionOutcome> {
  try {
    const response = await fetch(
      resolvePrimaryEnvironmentHttpUrl("/api/amoreg/space-document-version", { spaceId, docId }),
      {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ label }),
      },
    );
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      return { ok: false, message: spacesErrorMessage(text, VERSION_FALLBACK) };
    }
    const body = (await response.json()) as { version?: unknown };
    const version = toDocumentVersion(body.version);
    return version ? { ok: true, version } : { ok: false, message: VERSION_FALLBACK };
  } catch {
    return { ok: false, message: VERSION_FALLBACK };
  }
}
